import { useEffect } from 'react';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { useAgentBranch, TESTING_ENVIRONMENTS } from './utils';

/**
 * Git branch selector for deploy-agent templates.
 *
 * This field controls WHICH BRANCH the agent code is built from.
 *
 * - Production environments (prod, deprod, krprod, jpprod): locked to "main", input is disabled.
 * - Testing environments (dev, staging) + main branch agent: user can type any branch name.
 * - Testing environments (dev, staging) + feature branch agent: locked to the detected feature branch name.
 *
 * Branch detection is handled by the shared useAgentBranch hook.
 *
 * Uses: `ui:field: ConditionalGitRef` in template YAML.
 * Reads: `agent_ref` and `environment` from sibling form fields via formContext.
 */
export const ConditionalGitRefField = ({
  onChange,
  rawErrors,
  formContext,
  schema,
}: FieldExtensionComponentProps<string>) => {
  const formValues = formContext?.formData as Record<string, any> | undefined;
  const environment = formValues?.environment as string;
  const agentRef = formValues?.agent_ref as string | undefined;
  const currentValue = (formValues?.gitRef || '') as string;

  const { branch: detectedBranch, isFeatureBranch } = useAgentBranch(agentRef);

  // Only dev allows custom branch; all other environments lock to main
  const isEditable = !environment || TESTING_ENVIRONMENTS.includes(environment);

  // Force value to main when environment is not dev
  useEffect(() => {
    if (!isEditable && currentValue !== 'main') {
      onChange('main');
    }
  }, [isEditable, currentValue, onChange]);

  // Auto-set gitRef to the feature branch when agent is on a feature branch
  useEffect(() => {
    if (isFeatureBranch && detectedBranch && currentValue !== detectedBranch) {
      onChange(detectedBranch);
    }
  }, [isFeatureBranch, detectedBranch, currentValue, onChange]);

  const errorMessage = rawErrors?.[0];

  return (
    <FormControl
      margin="normal"
      error={rawErrors?.length > 0 && !rawErrors[0]?.startsWith('__no__error__')}
      fullWidth
    >
      <InputLabel htmlFor="gitRef" shrink>
        {schema.title || 'GitHub Revision'}
      </InputLabel>
      {isEditable ? (
        <Input
          id="gitRef"
          aria-describedby="gitRef-helper-text"
          value={currentValue}
          onChange={e => onChange(e.target?.value)}
          placeholder="Enter git branch"
          disabled={isFeatureBranch}
        />
      ) : (
        <Select
          id="gitRef"
          value="main"
          disabled
        >
          <MenuItem value="main">main</MenuItem>
        </Select>
      )}
      {errorMessage && (
        <FormHelperText id="gitRef-helper-text" error={rawErrors?.length > 0}>
          {errorMessage}
        </FormHelperText>
      )}
      {isFeatureBranch && (
        <FormHelperText id="gitRef-helper-text">
          Branch is locked to "{detectedBranch}" because this agent is registered from a feature branch.
        </FormHelperText>
      )}
      {!isEditable && (
        <FormHelperText id="gitRef-helper-text">
          Only dev deployments allow custom branches. All other environments use main.
        </FormHelperText>
      )}
    </FormControl>
  );
};
